import type { ContinuationTarget } from "~/helpers/pendingIntentLifecycle";
import type { ReceiverIdentityAdapter } from "~produce/production/helpers/continuationAdapter";

import { EVENTS_PATH } from "~produce/production/events/helpers/eventDefinitionsSsrmRow";

export const MATERIAL_EVENT_TARGET_KIND = "productionMaterialEvent";

export const SPACE_EVENT_TARGET_KIND = "productionSpaceEvent";

export const buildMaterialEventTarget = (productionMaterialEventId: string): ContinuationTarget => ({
	targetId: productionMaterialEventId,
	targetKind: MATERIAL_EVENT_TARGET_KIND
});

export const buildSpaceEventTarget = (productionSpaceEventId: string): ContinuationTarget => ({
	targetId: productionSpaceEventId,
	targetKind: SPACE_EVENT_TARGET_KIND
});

const MATERIAL_EVENT_ROUTE_PATTERN = /^\/produce\/production\/events\/material\/([^/?#]+)$/;

const SPACE_EVENT_ROUTE_PATTERN = /^\/produce\/production\/events\/space\/([^/?#]+)$/;

const matchSingleSegmentEventRoute = (
	pathname: string,
	pattern: RegExp,
	targetKind: string
): ContinuationTarget | null => {
	const match = pattern.exec(pathname);
	if (!match) return null;
	try {
		return { targetId: decodeURIComponent(match[1]), targetKind };
	} catch {
		return null;
	}
};

export const matchMaterialEventReceiverRoute = (pathname: string): ContinuationTarget | null =>
	matchSingleSegmentEventRoute(pathname, MATERIAL_EVENT_ROUTE_PATTERN, MATERIAL_EVENT_TARGET_KIND);

export const matchSpaceEventReceiverRoute = (pathname: string): ContinuationTarget | null =>
	matchSingleSegmentEventRoute(pathname, SPACE_EVENT_ROUTE_PATTERN, SPACE_EVENT_TARGET_KIND);

export const materialEventReceiverIdentityAdapter: ReceiverIdentityAdapter = {
	buildCleanPath: ({ targetId }) => `${EVENTS_PATH}/material/${encodeURIComponent(targetId)}`,
	matchesRoute: matchMaterialEventReceiverRoute
};

export const spaceEventReceiverIdentityAdapter: ReceiverIdentityAdapter = {
	buildCleanPath: ({ targetId }) => `${EVENTS_PATH}/space/${encodeURIComponent(targetId)}`,
	matchesRoute: matchSpaceEventReceiverRoute
};

export const MATERIAL_EVENT_DETAIL_TARGET_KIND = "materialEventDetail";

export const SPACE_EVENT_DETAIL_TARGET_KIND = "spaceEventDetail";

export const TASK_EVENT_DETAIL_TARGET_KIND = "taskEventDetail";

export const EVENT_DETAIL_TARGET_KINDS = [
	MATERIAL_EVENT_DETAIL_TARGET_KIND,
	SPACE_EVENT_DETAIL_TARGET_KIND,
	TASK_EVENT_DETAIL_TARGET_KIND
];

const ROUTE_SEGMENT_BY_TARGET_KIND: Record<string, string> = {
	[MATERIAL_EVENT_DETAIL_TARGET_KIND]: "material",
	[SPACE_EVENT_DETAIL_TARGET_KIND]: "space",
	[TASK_EVENT_DETAIL_TARGET_KIND]: "task"
};

const TARGET_KIND_BY_ROUTE_SEGMENT: Record<string, string> = {
	material: MATERIAL_EVENT_DETAIL_TARGET_KIND,
	space: SPACE_EVENT_DETAIL_TARGET_KIND,
	task: TASK_EVENT_DETAIL_TARGET_KIND
};

const EVENT_DETAIL_ROUTE_PATTERN =
	/^\/produce\/production\/events\/(material|space|task)\/([^/?#]+)\/details\/([^/?#]+)$/;

export interface EventDetailTarget {
	detailId: string;
	eventId: string;
}

export const eventDetailTargetId = (eventId: string, detailId: string): string => `${eventId}/${detailId}`;

export const parseEventDetailTargetId = (targetId: string): EventDetailTarget | null => {
	const [eventId, detailId, ...rest] = targetId.split("/");
	return eventId && detailId && rest.length === 0 ? { detailId, eventId } : null;
};

export const matchEventDetailReceiverRoute = (pathname: string): ContinuationTarget | null => {
	const match = EVENT_DETAIL_ROUTE_PATTERN.exec(pathname);
	if (!match) return null;
	try {
		return {
			targetId: eventDetailTargetId(decodeURIComponent(match[2]), decodeURIComponent(match[3])),
			targetKind: TARGET_KIND_BY_ROUTE_SEGMENT[match[1]]
		};
	} catch {
		return null;
	}
};

export const tabValueByName: Record<string, string> = {
	materials: "1",
	space: "3",
	tasks: "2"
};

export interface HeaderEventRouteParams {
	productionMaterialEventId?: string;
	productionSpaceEventId?: string;
	productionTaskEventId?: string;
}

export const headerEventTabValue = ({
	productionMaterialEventId,
	productionSpaceEventId,
	productionTaskEventId
}: HeaderEventRouteParams): string | undefined => {
	if (productionMaterialEventId) return tabValueByName.materials;
	if (productionSpaceEventId) return tabValueByName.space;
	if (productionTaskEventId) return tabValueByName.tasks;
	return undefined;
};

const EVENT_TAB_VALUE_BY_TARGET_KIND: Record<string, string> = {
	[MATERIAL_EVENT_DETAIL_TARGET_KIND]: tabValueByName.materials,
	[SPACE_EVENT_DETAIL_TARGET_KIND]: tabValueByName.space,
	[TASK_EVENT_DETAIL_TARGET_KIND]: tabValueByName.tasks
};

export const eventTabValueForRoute = (pathname: string): string | undefined => {
	const targetKind = matchEventDetailReceiverRoute(pathname)?.targetKind;
	return targetKind ? EVENT_TAB_VALUE_BY_TARGET_KIND[targetKind] : undefined;
};

export const buildEventDetailPath = ({ targetId, targetKind }: ContinuationTarget): string => {
	const target = parseEventDetailTargetId(targetId);
	const segment = ROUTE_SEGMENT_BY_TARGET_KIND[targetKind];
	if (!target || !segment) return "";
	return `${EVENTS_PATH}/${segment}/${encodeURIComponent(target.eventId)}/details/${encodeURIComponent(target.detailId)}`;
};

const eventDetailReceiverIdentityAdapter: ReceiverIdentityAdapter = {
	buildCleanPath: buildEventDetailPath,
	matchesRoute: matchEventDetailReceiverRoute
};

export default eventDetailReceiverIdentityAdapter;
